import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SealCheck } from "@phosphor-icons/react";
import { GoogleButton } from "@/components/GoogleButton";

const inputCls = "mt-1.5 h-12 rounded-[14px] border-ppc bg-ppc-bg text-ppc-text";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const dest = location.state?.from || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      toast.success(t("login_title"));
      navigate(dest, { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md flex-col px-6 pb-28 pt-12 md:pb-16">
      <h1 className="text-4xl leading-[1.08] tracking-[-0.02em] text-ppc-text sm:text-5xl">{t("login_title")}</h1>
      <p className="mt-2.5 text-sm text-ppc-muted">{t("hero_sub")}</p>

      <form onSubmit={submit} className="card-focal mt-8 space-y-4 p-6">
        <div>
          <Label htmlFor="email" className="text-ppc-muted">{t("email")}</Label>
          <Input id="email" type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
        </div>
        <div>
          <Label htmlFor="password" className="text-ppc-muted">{t("password")}</Label>
          <Input id="password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required className={inputCls} />
        </div>
        {err && <p className="inline-error" data-testid="login-error">{err}</p>}
        <Button type="submit" disabled={busy} data-testid="login-submit-btn" className="btn-primary h-[50px] w-full rounded-[14px] text-[15px]">
          {t("sign_in")}
        </Button>
        <GoogleButton label={t("google_btn")} />
      </form>

      <p className="mt-6 text-center text-sm text-ppc-muted">
        {t("no_account")}{" "}
        <Link to="/register" data-testid="go-register-link" className="text-primary-green">{t("sign_up")}</Link>
      </p>
      <p className="mt-auto flex items-center justify-center gap-1.5 pt-8 text-xs text-ppc-muted">
        <SealCheck size={15} weight="fill" color="#9184d9" /> Stripe · {t("app_tagline")}
      </p>
    </div>
  );
}
